/* eslint-disable react/jsx-no-useless-fragment */
import { MenuCategoriesProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import {
  getLastItemIndexOfCategory,
  getScaleSize,
  isBetweenRange,
  processItemsWithModifiers,
} from '../../../../../utils/utils';
import { memo } from 'react';
import Item from '../item';
import { TEMPLATE_1_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';
import { Grid as GridComponent } from '../../../../../base19/grid/Grid';

interface CategoryProps {
  template: Template;
  category: MenuCategoriesProps;
  categoryIndex?: number;
  development?: boolean;
  menuIndex: number;
  timezone?: string;
  scale?: number;
  tenantId?: number;
  column2FirstChild: string | null;
}

const Category = ({
  category,
  categoryIndex,
  template,
  development,
  menuIndex,
  timezone,
  scale = 1,
  tenantId,
  column2FirstChild,
}: CategoryProps) => {
  const { normalizePixels } = useNormalizedPixels();

  const sortedItems = processItemsWithModifiers(category.items, TEMPLATE_1_MODIFIERS);
  const isDevelopment = development ? true : isBetweenRange(category.schedules, timezone);
  const lastItemIndex = getLastItemIndexOfCategory(sortedItems) ?? -1;
  const hasAnyModifier = sortedItems.some((item) => item.hasValidModifiers);
  const isCategoryEnabled = template?.category?.isEnabled ?? true;
  const isModifier = (hasAnyModifier || sortedItems.length === 0) && template?.itemPrice?.isEnabled;
  const itemTextScale = template?.item?.textScale ?? 1;
  const media = template?.media?.mediaContent;
  const templateLayout = template.layoutType;
  const isMedia = template.media?.isEnabled && media?.url;
  const categoryId = `category-${category.id}`;
  const hasMargin = column2FirstChild === categoryId;
  // const fontFamily = extractFontFamilyFromUrl(template?.fontFamily);
  const fontFamily = template?.fontFamilyName;

  return (
    <>
      {isDevelopment && (
        <>
          {(isCategoryEnabled || isModifier) && (
            <BoxComponent
              id={categoryId}
              sx={{
                width: isMedia && templateLayout === 1 ? '48%' : '32%',
                overflow: 'hidden',
                height: 'auto',
                ...(hasMargin && isMedia && templateLayout === 2
                  ? { marginTop: getScaleSize(normalizePixels(530, 'height'), scale) }
                  : {}),
              }}
            >
              {isCategoryEnabled && (
                <TypographyComponent
                  variant="h3"
                  sx={{
                    color: template?.category?.color ? template?.category?.color : '#FFFEFE',
                    fontWeight: template.category?.fontWeight,
                    fontStyle: template.category?.fontStyle,
                    letterSpacing: template.category?.letterSpacing,
                    lineHeight: template.category?.lineHeight,
                    marginBottom: getScaleSize(normalizePixels(1.3, 'height'), scale),
                    borderBottom: '1px solid',
                    borderBottomColor: template.category?.underlineColor
                      ? template.category?.underlineColor
                      : '#FF8818',
                    textTransform: 'uppercase',
                    fontFamily: fontFamily,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    paddingBottom: getScaleSize(normalizePixels(5, 'height'), scale),
                    fontSize: getScaleSize(
                      normalizePixels(35 * Number(template?.category?.textScale ?? 1), 'height'),
                      scale
                    ),
                  }}
                >
                  {category.name}
                </TypographyComponent>
              )}

              {isModifier && (
                <BoxComponent
                  width={'100%'}
                  display="flex"
                  justifyContent="flex-end"
                  my={getScaleSize(normalizePixels(10, 'height'), scale)}
                >
                  <BoxComponent sx={{ width: `${35 * itemTextScale}% !important` }}>
                    <GridComponent container columnSpacing={1}>
                      {TEMPLATE_1_MODIFIERS.map((val) => (
                        <GridComponent key={val} item size={6}>
                          <TypographyComponent
                            sx={{
                              color: template.category?.underlineColor ? template.category?.underlineColor : '#FF8818',
                              fontWeight: '400',
                              textAlign: 'center',
                              fontFamily: fontFamily,
                              lineHeight: 'normal',
                              textTransform: 'uppercase',
                              fontSize: getScaleSize(normalizePixels(14 * itemTextScale, 'height'), scale),
                            }}
                          >
                            {val}
                          </TypographyComponent>
                        </GridComponent>
                      ))}
                    </GridComponent>
                  </BoxComponent>
                </BoxComponent>
              )}
            </BoxComponent>
          )}

          {sortedItems.map((item, itemIndex) => {
            if (item)
              return (
                <Item
                  key={`item-${item.id}-${categoryId}`}
                  template={template}
                  item={item}
                  lastItemIndex={lastItemIndex}
                  index={itemIndex}
                  development={development}
                  timezone={timezone}
                  scale={scale}
                  tenantId={tenantId}
                  hasAnyModifier={hasAnyModifier}
                  column2FirstChild={column2FirstChild}
                  categoryId={categoryId}
                />
              );
          })}
        </>
      )}
    </>
  );
};

export default memo(Category);
